/* SPDX-License-Identifier: AGPL-3.0-or-later */

import {Location, NgIf} from '@angular/common';
import {HttpErrorResponse} from '@angular/common/http';
import {Component, OnInit, input} from '@angular/core';
import {
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import {MatButtonModule} from '@angular/material/button';
import {MatCardModule} from '@angular/material/card';
import {DateAdapter, MAT_DATE_FORMATS, MAT_NATIVE_DATE_FORMATS} from '@angular/material/core';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatIconModule} from '@angular/material/icon';
import {MatInputModule} from '@angular/material/input';
import {MatListModule} from '@angular/material/list';
import {MatSnackBar} from '@angular/material/snack-bar';
import {Router, RouterLink} from '@angular/router';
import {TournamentEndpointService, TournamentOverviewDto, TournamentUpdateDto} from '@api';
import {isValidDate} from 'rxjs/internal/util/isDate';
import {CustomDateAdapter} from 'src/app/adapters/date-adapter';
import {MatCheckboxModule} from "@angular/material/checkbox";

@Component({
    selector: 'app-tournament-edit',
    standalone: true,
    imports: [
        MatFormFieldModule,
        MatInputModule,
        MatDatepickerModule,
        MatIconModule,
        MatButtonModule,
        MatListModule,
        MatCardModule,
        FormsModule,
        NgIf,
        ReactiveFormsModule,
        RouterLink,
        MatCheckboxModule,
    ],
    providers: [
        {provide: DateAdapter, useClass: CustomDateAdapter},
        {provide: MAT_DATE_FORMATS, useValue: MAT_NATIVE_DATE_FORMATS},
    ],
    templateUrl: './tournament-edit.component.html',
    styleUrl: './tournament-edit.component.scss',
})
export class TournamentEditComponent implements OnInit {
    tournamentId = input.required<number>();
    tournamentStarted: boolean = false;

    tournamentForm = new FormGroup({
        name: new FormControl('', [
            Validators.required,
            Validators.minLength(1),
            Validators.maxLength(200),
            Validators.pattern(/^[\w\s.äÄöÖüÜ\-,]*$/),
        ]),
        registrationEndDate: new FormControl<Date | undefined>(undefined, [Validators.required]),
        registrationEndTime: new FormControl('23:59', [Validators.required]),
        maxParticipants: new FormControl<number | undefined>(undefined, [Validators.required]),
        waitingListEnabled: new FormControl<boolean>(false, []),
        description: new FormControl('', []),
    });

    constructor(
        private service: TournamentEndpointService,
        private router: Router,
        private snackBar: MatSnackBar,
        protected dateAdapter: CustomDateAdapter,
        private location: Location,
    ) {
    }

    ngOnInit(): void {
        this.service.getTournamentOverview(this.tournamentId()).subscribe({
            next: (data: TournamentOverviewDto) => {
                const date = new Date(data.registrationEnd);
                this.tournamentForm.setValue({
                    name: data.name,
                    registrationEndDate: date,
                    registrationEndTime: `${this.getToDigitString(date.getHours())}:${this.getToDigitString(date.getMinutes())}`, // USe UTCHours ?
                    maxParticipants: data.maxParticipants,
                    waitingListEnabled: data.waitingListActivated,
                    description: data.description!,
                });
                if (this.tournamentStarted || data.allQualificationMatches > 0) {
                    this.tournamentStarted = true;
                    this.tournamentForm.get('maxParticipants')?.disable();
                    this.tournamentForm.get('registrationEndDate')?.disable();
                    this.tournamentForm.get('registrationEndTime')?.disable();
                }
            },
            error: async (err: HttpErrorResponse) => {
                this.defaultServiceErrorHandling(err);
            },
        });
    }

    get today() {
        return new Date();
    }

    onSave() {
        if (!this.tournamentForm.valid) {
            return;
        }
        this.tournamentForm.disable();

        const date = this.tournamentForm.controls.registrationEndDate.value!;
        date.setHours(parseInt(this.tournamentForm.controls.registrationEndTime.value!.split(':')[0]));
        date.setMinutes(
            parseInt(this.tournamentForm.controls.registrationEndTime.value!.split(':')[1]),
        );
        this.service
            .updateTournament(this.tournamentId(), {
                name: this.tournamentForm.controls.name.value!,
                registrationEnd: date.toISOString()!,
                maxParticipants: this.tournamentForm.controls.maxParticipants.value!,
                description: this.tournamentForm.controls.description.value,
                waitingListActivated: this.tournamentForm.controls.waitingListEnabled.value,
            } as TournamentUpdateDto)
            .subscribe({
                next: () => {
                    this.snackBar.open('Successfully updated tournament', 'Close', {
                        duration: 3000,
                    });
                    this.location.back();
                },
                error: async (er: HttpErrorResponse) => {
                    this.defaultServiceErrorHandling(er);
                    this.tournamentForm.get('name')?.enable();
                    this.tournamentForm.get('description')?.enable();
                    this.tournamentForm.get('waitingListEnabled')?.enable();
                    if (!this.tournamentStarted) {
                        this.tournamentForm.get('maxParticipants')?.enable();
                        this.tournamentForm.get('registrationEndDate')?.enable();
                        this.tournamentForm.get('registrationEndTime')?.enable();
                    }
                },
            });
    }

    isValidDateString(value: string) {
        return isValidDate(this.dateAdapter.parse(value));
    }

    back() {
        this.location.back();
    }

    private defaultServiceErrorHandling(error: HttpErrorResponse) {
        let errorMessage = '';
        console.log(JSON.stringify(error));
        if (Array.isArray(error.error?.errors)) {
            errorMessage = error.error.errors.join(', ');
        } else {
            if (!error.error) {
                errorMessage = 'fetching tournament';
            } else if (typeof error.error === 'object') {
                errorMessage = error.error.error;
            } else {
                errorMessage = error.error;
            }
        }
        this.snackBar.open('Error: ' + errorMessage, 'Close', {
            duration: 5000,
        });
    }

    getToDigitString(value: number): string {
        if (value < 10) {
            return '0' + value;
        }
        return value.toString();
    }
}
